import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useLayoutEffect, useRef } from 'react';
import {
  ImperativePanelHandle,
  Panel,
  PanelGroup,
  PanelResizeHandle,
} from 'react-resizable-panels';
import { useAppSelector } from '../../app/hooks';
import {
  selectFeatureFlagPosCart,
  selectIsShadowModeEnabled,
} from '../../redux/features/config/config.selector';
import { selectIsAgentModeLocked } from '../../redux/features/agentMode/agentMode.selector';
import { selectIsDraftCartEmpty } from '../../redux/features/draftCart/draftCart.selector';
import Colors from '../../utils/color';
import {
  getCartSplit,
  getRightStackHeight,
  saveCartSplit,
  saveRightStackHeight,
} from '../../utils/local-storage';
import {
  CARD_MAX_HEIGHT,
  CARD_MIN_HEIGHT,
  CART_SPLIT_MAX,
  CART_SPLIT_MIN,
} from '../Layout/LayoutPreferenceDialog';
import { OrderActions } from '../OrderReview/OrderActions';
import AgentCartCard from './AgentCartCard';
import MyCartCard from './MyCartCard';
import OrderTotalCard from './OrderTotalCard';
import PosCartCard from './PosCartCard';
import useRightStackCollapse from './hooks/useRightStackCollapse.hooks';
import useStackLayout from './hooks/useStackLayout.hooks';

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  // Right pane wrapper (mirrors LeftSidebar's leftCard): a flush column holding
  // the Agent Cart / POS Cart stack + the pinned totals / Cancel-Finish footer.
  // No outer frame or padding — sections attach edge-to-edge.
  outer: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  // Holds the resizable card stack; the footer sits below it (pinned).
  panelRegion: {
    flex: 1,
    minHeight: 0,
  },
  // Wraps each card inside a vertical panel: fills the panel height so each
  // card's OWN internal scroll works; overflow hidden (not auto) so no outer
  // scrollbar appears beside a collapsed hug-height card.
  stackPane: {
    height: '100%',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'hidden',
    overflowX: 'hidden',
  },
  // Hairline divider between the Agent Cart / POS Cart cards (mirrors
  // LeftSidebar's vResizeHandle): a 1px rule with NO white gutter so the card
  // headers butt straight up against the line. The drag hit area (invisible
  // ::before) and grip pill (::after) live in pseudo-elements so the visible
  // line stays 1px while staying easy to grab.
  vResizeHandle: {
    position: 'relative',
    zIndex: 1,
    height: 1,
    flexShrink: 0,
    outline: 'none',
    backgroundColor: Colors.alto,
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      top: spacing(-0.75),
      bottom: spacing(-0.75),
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 1,
      height: 3,
      width: spacing(3.5),
      borderRadius: 2,
      backgroundColor: Colors.silverChalice,
      transition: 'background-color 120ms ease, width 120ms ease',
    },
    '&:hover::after, &[data-resize-handle-active]::after': {
      backgroundColor: Colors.readBack,
      width: spacing(5),
    },
  },
  // HITL-1614: the horizontal twin of vResizeHandle, dividing My Cart from the
  // Live Order. Same 1px rule + invisible hit area + grip pill, rotated.
  //
  // HITL-1648 made it findable. The resize always worked; nobody could tell it
  // was there. Three changes, all at REST rather than on hover — an affordance
  // only a hover reveals is one the agent has to already suspect:
  //
  // - `cursor: col-resize` on the handle and its whole hit area, so the pointer
  //   says "drag me" before anything is pressed;
  // - a wider resting pill that grows further on hover, so it reads as a grip
  //   rather than as the end of a border;
  // - a visible keyboard focus ring, since the handle is focusable and was
  //   `outline: none` with nothing put back.
  hResizeHandle: {
    position: 'relative',
    zIndex: 1,
    width: 1,
    flexShrink: 0,
    outline: 'none',
    cursor: 'col-resize',
    backgroundColor: Colors.alto,
    '&:focus-visible::after': {
      backgroundColor: Colors.readBack,
      boxShadow: `0 0 0 2px ${Colors.white}, 0 0 0 4px ${Colors.readBack}`,
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: spacing(-0.75),
      right: spacing(-0.75),
      cursor: 'col-resize',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 1,
      width: 4,
      height: spacing(3.5),
      borderRadius: 2,
      backgroundColor: Colors.silverChalice,
      transition: 'background-color 120ms ease, height 120ms ease',
    },
    '&:hover::after, &[data-resize-handle-active]::after': {
      backgroundColor: Colors.readBack,
      height: spacing(6),
    },
  },
  // Each column inside the horizontal split; mirrors stackPane so a card's own
  // internal scroll still works and no outer scrollbar appears.
  columnPane: {
    height: '100%',
    minWidth: 0,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  // Pinned footer: subtotal / tax / total + Cancel / Finish. Sits BELOW the
  // resizable stack so it stays visible however the cards are resized or
  // collapsed — the whole point of splitting the carts into their own panels.
  // Flush like the sections above: attached by a single top rule, no card frame.
  footer: {
    flexShrink: 0,
    backgroundColor: Colors.white,
    borderTop: `1px solid ${Colors.alto}`,
    overflow: 'hidden',
  },
}));

interface RightSidebarProps {
  // Per-agent storage key; the nested vertical stack persists under a suffix.
  paneStorageKey: string;
}

/**
 * RIGHT SIDEBAR of the HITL console. A drag-to-resizable vertical stack of the
 * Agent Cart and POS Cart cards (sizes persist per agent via autoSaveId, the
 * vertical twin of LeftSidebar), with the order totals + Cancel / Finish actions
 * pinned in a footer below the stack so they never scroll away. The POS Cart
 * card is present only when the pos_cart flag is on. Collapse-sync between each
 * card's chevron and its panel lives in useRightStackCollapse.
 */
const RightSidebar = ({ paneStorageKey }: RightSidebarProps) => {
  const classes = useStyles();
  const isPosCartEnabled = useAppSelector(selectFeatureFlagPosCart);
  // HITL-1614: the flag alone decides whether there is a second column. It is
  // deliberately not a question about the mode — the agent builds in `Shadow`
  // and promotes in `Intercept`, so My Cart is on screen in both. With
  // `hitl_shadow_mode` off no nested PanelGroup is constructed and this file
  // renders exactly as it does in production today.
  const isShadowModeEnabled = useAppSelector(selectIsShadowModeEnabled);
  // HITL-1615: once My Cart is empty in `Intercept` the agent has promoted
  // everything, so the column gives its width back. It COLLAPSES — the panel
  // stays mounted and is resized to zero. Unmounting a sibling would risk
  // remounting the panel that holds `OrderReview`, whose mount effect dispatches
  // `sendOrder` unguarded, and `Intercept` is the one mode where that sink is
  // open. A layout decision must never put an order on the POS.
  //
  // HITL-1650 narrowed WHICH Intercept, and the original wording says why: "the
  // agent has promoted everything". That reads the empty cart as the end of a
  // job. Now that the console can arm `Intercept` off the AI's take-over alert,
  // an empty My Cart also means the opposite — a cart nobody has touched yet —
  // and collapsing it there would take the column away at the exact moment the
  // AI escalated, rearranging the screen under an agent who has decided nothing
  // and may still step back to `Shadow`.
  //
  // `selectIsAgentModeLocked` is the honest condition: the Intercept is theirs
  // to keep, either because they chose it or because something has already gone
  // out on the order. Both of those mean the empty cart is a finished job. It is
  // the same predicate the mode switch locks on, which is the point — the column
  // gives up its width exactly when `Shadow` stops being one press away.
  const isAgentModeLocked = useAppSelector(selectIsAgentModeLocked);
  const isDraftCartEmpty = useAppSelector(selectIsDraftCartEmpty);
  const shouldCollapseMyCart = isAgentModeLocked && isDraftCartEmpty;
  const myCartPanelRef = useRef<ImperativePanelHandle>(null);

  // useLayoutEffect, matching useRightStackCollapse: apply the resize in the
  // same paint as the render that caused it, so promoting the last line does not
  // read as a two-phase animation.
  useLayoutEffect(() => {
    const panel = myCartPanelRef.current;
    if (!panel) return;
    if (shouldCollapseMyCart && panel.isExpanded()) panel.collapse();
    else if (!shouldCollapseMyCart && panel.isCollapsed()) panel.expand();
  }, [shouldCollapseMyCart]);

  const {
    agentOpen,
    setAgentOpen,
    posOpen,
    setPosOpen,
    agentPanelRef,
    posPanelRef,
  } = useRightStackCollapse();
  // Card-height persistence + live dialog control for this stack. POS Cart opens
  // at the default [75, 25] split; when a saved split exists, restoreOnMount
  // applies it over that default on mount.
  // HITL-1648: the My Cart | Live order split, persisted the same way the card
  // heights are. `useStackLayout` is axis-agnostic — it holds a two-panel group,
  // a saved [a, b] summing to 100, a clamp, and the dialog's live-apply setter —
  // so the horizontal split reuses it rather than growing a second mechanism
  // beside it. Only a CLEAN split is stored (both columns within the clamp),
  // which is exactly what keeps a collapsed My Cart from being remembered as a
  // width preference.
  const {
    groupRef: cartGroupRef,
    reportSizes: reportCartSizes,
    persistOnDragEnd: persistCartSplit,
  } = useStackLayout({
    region: 'carts',
    getSaved: getCartSplit,
    saveSaved: saveCartSplit,
    // No stale autosave to clean up: this split never had an `autoSaveId`.
    staleAutoSaveKey: `react-resizable-panels:${paneStorageKey}:carts`,
    restoreOnMount: true,
    min: CART_SPLIT_MIN,
    max: CART_SPLIT_MAX,
  });

  const { groupRef, reportSizes, persistOnDragEnd } = useStackLayout({
    region: 'right',
    getSaved: getRightStackHeight,
    saveSaved: saveRightStackHeight,
    staleAutoSaveKey: `react-resizable-panels:${paneStorageKey}:right-stack`,
    restoreOnMount: true,
    min: CARD_MIN_HEIGHT,
    max: CARD_MAX_HEIGHT,
  });

  return (
    <div className={classes.outer}>
      <div className={classes.panelRegion}>
        {/* Heights persist per agent via the dedicated `hitl-layout-height-right`
            key (drag via persistOnDragEnd, dialog via Apply) — no autoSaveId, so
            there's a single source of truth. */}
        <PanelGroup direction="vertical" ref={groupRef} onLayout={reportSizes}>
          <Panel
            id="agent-cart"
            order={1}
            ref={agentPanelRef}
            collapsible
            collapsedSize={8}
            defaultSize={isPosCartEnabled ? 75 : 100}
            minSize={15}
            onCollapse={() => setAgentOpen(false)}
            onExpand={() => setAgentOpen(true)}
          >
            <div className={classes.stackPane}>
              {isShadowModeEnabled ? (
                /*
                  HITL-1614 — My Cart | Live Order.

                  Neither Panel is ever conditionally mounted, and the mode is
                  not read here at all. The Live Order Panel holds OrderReview,
                  whose mount effect at OrderReview.tsx:174 dispatches
                  `sendOrder` with no `validCartItems.length` guard — so
                  remounting it on a mode change would put an order on the POS as
                  a side effect of a layout decision. That is the shape of the
                  dropped-order incident, and it is why nothing about this tree
                  varies once the flag is on.

                  `order` is set on both for react-resizable-panels' own
                  bookkeeping.

                  HITL-1648: the split IS persisted now, through
                  LayoutPreferenceContext's new `carts` region — its own
                  local-storage key and its own row in the Layout preference
                  dialog, so a drag and an explicit Apply write to the same
                  place. It survives a reload and the next session.
                */
                <PanelGroup
                  direction="horizontal"
                  ref={cartGroupRef}
                  onLayout={reportCartSizes}
                >
                  <Panel
                    id="my-cart"
                    order={1}
                    ref={myCartPanelRef}
                    collapsible
                    collapsedSize={0}
                    defaultSize={50}
                    minSize={20}
                  >
                    <div className={classes.columnPane}>
                      <MyCartCard />
                    </div>
                  </Panel>
                  <PanelResizeHandle
                    className={classes.hResizeHandle}
                    onDragging={persistCartSplit}
                  />
                  <Panel
                    id="live-order"
                    order={2}
                    defaultSize={50}
                    minSize={20}
                  >
                    <div className={classes.columnPane}>
                      <AgentCartCard
                        open={agentOpen}
                        onOpenChange={setAgentOpen}
                      />
                    </div>
                  </Panel>
                </PanelGroup>
              ) : (
                <AgentCartCard open={agentOpen} onOpenChange={setAgentOpen} />
              )}
            </div>
          </Panel>

          {isPosCartEnabled && (
            <>
              <PanelResizeHandle
                className={classes.vResizeHandle}
                onDragging={persistOnDragEnd}
              />
              <Panel
                id="pos-cart"
                order={2}
                ref={posPanelRef}
                collapsible
                collapsedSize={8}
                defaultSize={25}
                minSize={15}
                onCollapse={() => setPosOpen(false)}
                onExpand={() => setPosOpen(true)}
              >
                <div className={classes.stackPane}>
                  <PosCartCard open={posOpen} onOpenChange={setPosOpen} />
                </div>
              </Panel>
            </>
          )}
        </PanelGroup>
      </div>

      {/* Pinned footer — totals + Cancel / Finish, always visible. The action
          row inside carries the shared bottom-section floor (HITL-1665). */}
      <div className={classes.footer}>
        <OrderTotalCard />
        <OrderActions />
      </div>
    </div>
  );
};

export default RightSidebar;
